import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeName, setActiveName] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    setActiveId((current) => {
      if (current || data.length === 0) return current;
      setActiveName(data[0].user_name);
      return data[0].user_id;
    });
  }, []);

  const loadMessages = useCallback(async (uid) => {
    const { data } = await api.get(`/messages/${uid}`);
    setMessages(data);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post("/messages", { to_user_id: activeId, content: text });
    setText("");
    loadMessages(activeId);
    loadThreads();
  };

  return (
    <div data-testid="messages-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="overline mb-3">Private chat</div>
      <h1 className="font-serif-display text-4xl md:text-5xl mb-10">Messages</h1>

      <div className="grid md:grid-cols-12 gap-6 h-[68vh] border border-[var(--line)] rounded-sm overflow-hidden">
        <aside className="md:col-span-4 border-r border-[var(--line)] overflow-y-auto" style={{ background: "var(--bg-alt)" }}>
          {threads.length === 0 ? (
            <div className="p-6 text-sm text-[var(--ink-soft)]">No conversations yet.</div>
          ) : (
            threads.map((t) => (
              <button
                key={t.user_id}
                data-testid={`thread-${t.user_id}`}
                onClick={() => { setActiveId(t.user_id); setActiveName(t.user_name); }}
                className={`w-full text-left p-5 border-b border-[var(--line)] hover:bg-white/60 transition-colors ${activeId === t.user_id ? "bg-white" : ""}`}
              >
                <div className="font-medium text-sm">{t.user_name}</div>
                <div className="text-xs text-[var(--ink-soft)] mt-1 truncate">{t.last_message || "Start a conversation…"}</div>
              </button>
            ))
          )}
        </aside>

        <section className="md:col-span-8 flex flex-col">
          <div className="px-6 py-4 border-b border-[var(--line)]">
            <div className="font-serif-display text-xl">{activeName || "Select a conversation"}</div>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
            {messages.length === 0 ? (
              <div className="text-sm text-[var(--ink-soft)] text-center py-10">No messages yet. Say hello!</div>
            ) : (
              messages.map((m) => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-[var(--terracotta)] text-[var(--ink)] rounded-br-sm" : "bg-[var(--bg-alt)] text-[var(--ink)] rounded-bl-sm"}`}>
                      {m.content}
                      <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[var(--ink-soft)]"} font-mono-acc`}>
                        {m.created_at?.slice(11, 16)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>
          {activeId && (
            <form onSubmit={send} className="border-t border-[var(--line)] p-4 flex gap-2">
              <input
                data-testid="message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 px-4 py-2.5 border border-[var(--line)] rounded-full text-sm focus:outline-none focus:border-[var(--terracotta)]"
              />
              <button data-testid="message-send" type="submit"
                className="bg-[var(--terracotta)] text-[var(--ink)] px-5 rounded-full text-sm hover:bg-[var(--terracotta-dark)] inline-flex items-center gap-2">
                <Send className="w-4 h-4" /> Send
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
